import { z } from "zod";

import { SMS_TITLE_MAX_BYTES, smsByteLength } from "@/shared/utils/sms-bytes";

export const ConsultationTarget = {
	PATIENT: 'patient',
	GUARDIAN: 'guardian',
} as const;
export type ConsultationTarget = (typeof ConsultationTarget)[keyof typeof ConsultationTarget];

// ── Shared field rules (MED.001, 2026-07-09) ──
// Charset = basic + general special chars, NO emoji, for every text field.
const EMOJI_RE = /\p{Extended_Pictographic}/u;
const hasEmoji = (v: string) => EMOJI_RE.test(v);
const hasLineBreak = (v: string) => /[\r\n]/.test(v);

// ── Consultation Note Schema ──
// Base object holds the raw fields; per-type + per-field rules are applied in superRefine
// so the correct message (message body vs call summary) and required/length limits apply.
export const consultationNoteSchema = z
	.object({
		type: z.enum(["message", "call"]),
		// MED.002: all selection controls mandatory, no default value.
		consultationTarget: z.enum([ConsultationTarget.PATIENT, ConsultationTarget.GUARDIAN]).optional(),

		// Call-specific
		callDateTime: z.string().optional(),
		// 통화 방향 (OUT 발신 / IN 수신) — PRD 04 §3.4 + prototype; BE ConsultationNote.callDirection.
		// (BA decision 2026-07-23: follow BE `callDirection`, not the provisional 결과 field.)
		callDirection: z.enum(["OUT", "IN"]).optional(),
		callDuration: z.number().optional(),

		// Message-specific
		appMessage: z.boolean().optional(),
		smsMessage: z.boolean().optional(),

		// Common text — validated on the TRIMMED value (MED.001 global rule)
		title: z.string().optional(),
		text: z.string().optional(),
	})
	.superRefine((v, ctx) => {
		// ── Consultation Target: required, no default (MED.002) ──
		if (!v.consultationTarget)
			ctx.addIssue({
				path: ["consultationTarget"],
				code: z.ZodIssueCode.custom,
				message: "validation:common.required",
			});

		// ── Title / Subject: optional, max 100, no emoji, no line breaks ──
		// SHCT-846 — a title sent by SMS is additionally capped at 40 BYTES, because the carrier caps it
		// there: SOLAPI rejects a longer LMS subject with status 1014 ("제목은 40byte까지 사용 가능합니다"),
		// and before this the field accepted 100 characters and the network silently truncated the rest —
		// the staff member saw a full title on screen and the patient received about 20 characters of it.
		//
		// Scoped to the SMS channel on purpose (customer decision, 2026-08-18). The 40 bytes are a carrier
		// limit, not a product one, so an APP-only message keeps the PRD 04 §3.4 / [공통] 00 §3 TITLE
		// allowance of 100 characters; capping it too would cost 60% of the title for no technical reason.
		// A call note is never sent by SMS and is untouched.
		const title = (v.title ?? "").trim();
		if (title) {
			if (title.length > 100)
				ctx.addIssue({
					path: ["title"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.maxLength",
				});
			// Ticking SMS on an already-typed long title surfaces the error at that moment, which is the
			// honest outcome: the title only becomes un-sendable once SMS is the channel.
			if (v.type === "message" && v.smsMessage && smsByteLength(title) > SMS_TITLE_MAX_BYTES)
				ctx.addIssue({
					path: ["title"],
					code: z.ZodIssueCode.custom,
					message: "validation:message.titleSmsBytes",
				});
			if (hasEmoji(title))
				ctx.addIssue({
					path: ["title"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.noEmoji",
				});
			if (hasLineBreak(title))
				ctx.addIssue({
					path: ["title"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.noLineBreak",
				});
		}

		// ── Content / Call Summary: required 1..1000, no emoji, line breaks allowed ──
		const text = (v.text ?? "").trim();
		if (text.length < 1) {
			ctx.addIssue({
				path: ["text"],
				code: z.ZodIssueCode.custom,
				message:
					v.type === "call"
						? "validation:call.summaryRequired"
						: "validation:message.bodyRequired",
			});
		} else {
			if (text.length > 1000)
				ctx.addIssue({
					path: ["text"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.maxLength",
				});
			if (hasEmoji(text))
				ctx.addIssue({
					path: ["text"],
					code: z.ZodIssueCode.custom,
					message: "validation:common.noEmoji",
				});
		}

		if (v.type === "message") {
			// MED.002: at least one delivery channel required, no default.
			if (!v.appMessage && !v.smsMessage)
				ctx.addIssue({
					path: ["appMessage"],
					code: z.ZodIssueCode.custom,
					message: "validation:message.channelRequired",
				});
		}

		if (v.type === "call") {
			// Call Date & Time: required + must NOT be in the future (PD-08 / FE-19).
			if (!v.callDateTime)
				ctx.addIssue({
					path: ["callDateTime"],
					code: z.ZodIssueCode.custom,
					message: "validation:call.dateTimeRequired",
				});
			else if (new Date(v.callDateTime.replace(" ", "T")) > new Date())
				ctx.addIssue({
					path: ["callDateTime"],
					code: z.ZodIssueCode.custom,
					message: "validation:call.dateTimePast",
				});
			// Call Direction (방향): required, OUT (발신) | IN (수신), no default selection
			// (MED.002 / PRD 04 §3.4; BE ConsultationNote.callDirection enum OUT/IN).
			if (v.callDirection !== "OUT" && v.callDirection !== "IN")
				ctx.addIssue({
					path: ["callDirection"],
					code: z.ZodIssueCode.custom,
					message: "validation:call.directionRequired",
				});
			// Call Duration: required, integer 0..120, zero allowed, no decimals (MED.003).
			if (v.callDuration == null || Number.isNaN(v.callDuration))
				ctx.addIssue({
					path: ["callDuration"],
					code: z.ZodIssueCode.custom,
					message: "validation:call.durationRequired",
				});
			else if (!Number.isInteger(v.callDuration))
				ctx.addIssue({
					path: ["callDuration"],
					code: z.ZodIssueCode.custom,
					message: "validation:call.durationInteger",
				});
			else if (v.callDuration < 0 || v.callDuration > 120)
				ctx.addIssue({
					path: ["callDuration"],
					code: z.ZodIssueCode.custom,
					message: "validation:call.durationRange",
				});
		}
	});

export type ConsultationNoteFormValues = z.infer<typeof consultationNoteSchema>;

/** Default values for a new consultation note — MED.002: NO default selection. */
export const DEFAULT_CONSULTATION_NOTE: ConsultationNoteFormValues = {
	type: "message",
	consultationTarget: undefined,
	callDateTime: "",
	callDirection: undefined,
	callDuration: undefined,
	appMessage: false,
	smsMessage: false,
	title: "",
	text: "",
};

/** Get default values, deriving consultationTarget from the patient's appUser.
 * `appUser` is from `PatientListItem.appUser`.
 * MED.002: selection is automatic — field is read-only in the form. */
export function getDefaultNote(appUser?: ConsultationTarget): ConsultationNoteFormValues {
	return {
		...DEFAULT_CONSULTATION_NOTE,
		consultationTarget:
			appUser === ConsultationTarget.GUARDIAN
				? ConsultationTarget.GUARDIAN
				: ConsultationTarget.PATIENT,
	};
}

/**
 * Map a ConsultationNote (mock/API) to form values for editing/viewing.
 */
export function noteToFormValues(note: {
	type: "call" | "message";
	consultationTarget?: ConsultationTarget;
	date?: string;
	time?: string;
	callDirection?: "OUT" | "IN";
	callDuration?: number;
	messageChannels?: ("APP" | "SMS")[];
	title?: string;
	text: string;
}): ConsultationNoteFormValues {
	return {
		type: note.type,
		consultationTarget: note.consultationTarget ?? ConsultationTarget.PATIENT,
		callDateTime: note.date && note.time ? `${note.date} ${note.time}` : "",
		callDirection: note.callDirection,
		callDuration: note.callDuration,
		appMessage: (note.messageChannels ?? []).includes("APP"),
		smsMessage: (note.messageChannels ?? []).includes("SMS"),
		title: note.title ?? "",
		text: note.text ?? "",
	};
}
